import { useId, type MouseEvent, type ReactNode } from 'react';
import { Checkbox } from '../../atoms/Checkbox/Checkbox';
import styles from './FilterChipMenuItem.module.css';

/**
 * Figma: `_filter chip dropdown menu item` (93:1471) — "Internal sub-component
 * used inside the Filter Chip dropdown. Renders a selectable row with a checkbox
 * and label text. Variants: State (Default, Hover) × Selected (True, False)."
 *
 * The `_` prefix marks it internal, so it is not part of the package surface —
 * compose it through `FilterChip`.
 *
 * Figma places a bare `Checkbox` (its `Text=False` variant) beside a separate
 * text layer rather than using the checkbox's own label, because this row's
 * label is `Text sm/leading-none` and centred, where the Checkbox atom's label
 * is `leading-normal` and top-aligned. That is reproduced here: the bare control
 * comes from `Checkbox`, and the text is a sibling `<label htmlFor>`.
 *
 * Not in Figma: the whole row is a hit target, not just the checkbox and the
 * text. Both of those inner labels are `pointer-events: none` so a real click
 * anywhere in the row — including the padding and the badge slot — hits this
 * component's own `onClick` instead of being swallowed by whichever inner label
 * the pointer happened to land on. The `htmlFor` association is untouched, so
 * the checkbox still gets its accessible name and Tab/Space keep working.
 * `event.detail === 0` tells a keyboard-activated click (Space/Enter on the
 * focused input) apart from a real pointer click — that click already toggled
 * the box through `Checkbox`'s own `onChange`, so this handler ignores it
 * rather than toggling a second time.
 */
export type FilterChipMenuItemState = 'default' | 'hover';

/**
 * Figma models the `Select all` row as its own component set,
 * `_filter chip dropdown menu item select all` (215:6667). It was diffed
 * variant by variant against 93:1471 and the *only* difference across all four
 * is the text style — `Text sm/leading-none/Medium` against `…/Regular`. Size,
 * padding, radius, gap, the fixed 168px text frame, the hover surface, the text
 * colour and the checkbox properties are identical.
 *
 * So the two share this component rather than being duplicated, which is the
 * same call `SidebarRow` makes for `_sidebar item` and `_sidebar menu item`.
 * If the designer diverges them further, this is where the next difference
 * hangs.
 */
export type FilterChipMenuItemWeight = 'regular' | 'medium';

export type FilterChipMenuItemProps = {
  label: string;
  checked?: boolean;
  /** Forwarded to `Checkbox`. Not in Figma — see that component. */
  indeterminate?: boolean;
  onChange?: (checked: boolean) => void;
  /** Figma keeps a `Badge` slot after the label. Hidden in every variant. */
  badge?: ReactNode;
  /**
   * `regular` is `_filter chip dropdown menu item` (93:1471); `medium` is
   * `_filter chip dropdown menu item select all` (215:6667). The label's font
   * weight is the whole of the difference.
   */
  weight?: FilterChipMenuItemWeight;
  /** Force the hover appearance for documentation and visual regression only. */
  state?: FilterChipMenuItemState;
  id?: string;
  className?: string;
};

export function FilterChipMenuItem({
  label,
  checked = false,
  indeterminate = false,
  onChange,
  badge,
  weight = 'regular',
  state,
  id,
  className,
}: FilterChipMenuItemProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;

  const classes = [
    styles.item,
    state === 'hover' ? styles.hover : null,
    // Live hover only when no state is forced, so `:hover` reacts in the app and
    // stays inert in a states story.
    state === undefined ? styles.interactive : null,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const handleRowClick = (event: MouseEvent<HTMLDivElement>) => {
    if (event.detail === 0) return; // keyboard activation — Checkbox's own onChange already fired
    onChange?.(!checked);
  };

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- keyboard is handled by the composite widget (roving focus / native control); the pointer handler is a convenience. Axe checks run in Storybook and E2E.
    <div className={classes} onClick={handleRowClick}>
      <span className={styles.content}>
        <span className={styles.textAndIcon}>
          <Checkbox
            id={inputId}
            checked={checked}
            indeterminate={indeterminate}
            onChange={onChange}
            className={styles.checkboxControl}
          />
          <label
            className={[styles.label, weight === 'medium' ? styles.labelMedium : null]
              .filter(Boolean)
              .join(' ')}
            htmlFor={inputId}
          >
            {label}
          </label>
          {badge}
        </span>
      </span>
    </div>
  );
}
